import React, { useMemo, useCallback } from "react";
import "./OrderItemList.css";
import { useOrderItems } from "./hooks/useOrderItems";
import { discountUtils } from "./utils/discountUtils";
import { variantUtils } from "./utils/variantUtils";
import { styleUtils } from "./utils/styleUtils";
import { messageUtils } from "./utils/messageUtils";
import ItemImage from "./components/ItemImage";
import QuantityControls from "./components/QuantityControls";
import ItemTotal from "./components/ItemTotal";
import { useStoreSettings } from "../../../hooks/useStoreSettings";
import useCurrency from "../../../hooks/useCurrency";
import { useLanguage } from "../../../context/LanguageContext";
import { getLocalizedProductName } from "../../../utils/multilingualUtils";

const OrderItemsList = ({
  order = null,
  items = null,
  variant = "default",
  showQuantity = true,
  showVariants = true,
  showItemTotal = true,
  showWishlistToggle = false,
  showRemoveButton = false,
  showQuantityControls = false,
  showDiscountInfo = true,
  className = "",
  onItemClick = null,
  onQuantityChange = null,
  onRemoveItem = null,
  WishlistComponent = null,
  loading = false,
  disableRTL = false, // New prop to disable RTL layout
}) => {
  // Get store settings and currency formatting
  const { currency: storeCurrency, storeSettings } = useStoreSettings();
  const { formatDisplay, getCurrency } = useCurrency();
  const { language } = useLanguage();

  // Memoized values
  const orderItems = useOrderItems(items, order);

  const currencyFormatter = formatDisplay;

  const containerClasses = useMemo(
    () => styleUtils.getContainerClasses(variant, className, disableRTL),
    [variant, className, disableRTL]
  );

  // Memoized event handlers
  const handleItemClick = useCallback(
    (item, index) => {
      if (onItemClick) {
        onItemClick(item, index);
      }
    },
    [onItemClick]
  );

  const handleQuantityChange = useCallback(
    async (item, newQuantity) => {
      if (onQuantityChange) {
        await onQuantityChange(
          item.productId || item.id,
          newQuantity,
          item.variantId
        );
      }
    },
    [onQuantityChange]
  );

  const handleRemoveItem = useCallback(
    async (item) => {
      if (onRemoveItem) {
        await onRemoveItem(item.productId || item.id, item.variantId);
      }
    },
    [onRemoveItem]
  );

  // Loading state
  if (loading) {
    return (
      <div
        className={containerClasses}
        role="status"
        aria-label="Loading items"
      >
        <div className="loading-message">
          {messageUtils.getLoadingMessage(language)}
        </div>
      </div>
    );
  }

  // Empty state
  if (orderItems.length === 0) {
    return (
      <div className={containerClasses}>
        <div className="no-items-message" role="status">
          {messageUtils.getEmptyStateMessage(variant, language)}
        </div>
      </div>
    );
  }

  return (
    <div className={containerClasses} role="list" aria-label="Order items">
      {orderItems.map((item, index) => {
        const variantDisplayText = variantUtils.getDisplayText(item, language);
        const hasDiscount = discountUtils.hasDiscount(item);
        const itemClasses = styleUtils.getItemClasses(
          variant,
          onItemClick,
          hasDiscount
        );

        // Get localized product name
        const localizedProductName =
          getLocalizedProductName(item, language) || item.name || "Product";

        return (
          <div
            key={item.variantId || `${item.productId}-${index}`}
            className={itemClasses}
            onClick={() => handleItemClick(item, index)}
            role="listitem"
            tabIndex={onItemClick ? 0 : -1}
            onKeyDown={(e) => {
              if (onItemClick && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                handleItemClick(item, index);
              }
            }}
            aria-label={`${localizedProductName} - ${currencyFormatter(
              (item.price || 0) * (item.quantity || 1)
            )}`}
          >
            {/* Item Image Container */}
            <ItemImage
              item={item}
              showDiscountInfo={showDiscountInfo}
              showWishlistToggle={showWishlistToggle}
              WishlistComponent={WishlistComponent}
            />

            {/* Item Info */}
            <div className="item-info">
              <div
                className={`item-info-wrapper ${
                  language === "ar" ? "rtl" : "ltr"
                }`}
              >
                <div className="item-main">
                  <h4 className="item-name">{localizedProductName}</h4>
                  <div className="item-details">
                    {showVariants && variantDisplayText && (
                      <div className="item-variants">
                        <span className="variant-info">
                          {variantDisplayText}
                        </span>
                      </div>
                    )}
                    {showQuantityControls && (
                      <QuantityControls
                        item={item}
                        onQuantityChange={handleQuantityChange}
                      />
                    )}
                  </div>
                </div>

                {showItemTotal && (
                  <div className="item-meta">
                    <ItemTotal
                      item={item}
                      currencyFormatter={currencyFormatter}
                      showDiscountInfo={showDiscountInfo}
                      showQuantityControls={showQuantityControls}
                      showQuantity={showQuantity}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Remove Button - positioned absolutely within order-item container */}
            {showRemoveButton && (
              <button
                className="remove-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveItem(item);
                }}
                aria-label={`Remove ${
                  localizedProductName || "item"
                } from cart`}
                title="Remove item"
              >
                ×
              </button>
            )}

            {/* Stock status for checkout variant */}
            {variant === "checkout" &&
              item.stock !== undefined &&
              item.stock < 5 && (
                <div className="stock-warning" role="alert">
                  {item.stock === 0
                    ? "Out of stock"
                    : `Only ${item.stock} left`}
                </div>
              )}
          </div>
        );
      })}
    </div>
  );
};

export default OrderItemsList;
